import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Target, Flag, Bell, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function MarketingOptionsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Marketing Options</h1>
        <p className="text-lg text-gray-700 mb-6">
          The marketing options help you create content that not only ranks well in search engines but also drives conversions and business results.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="bg-blue-100 p-3 rounded-lg text-blue-700">
            <Bell className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Pro Feature</h3>
            <p className="text-blue-700">
              Marketing options are available on the Pro plan and above. These features help you create content specifically designed to drive business results.
            </p>
          </div>
        </div>
      </div>

      <div className="prose max-w-none">
        <h2>Marketing Options Explained</h2>
        
        <h3>Target Audience</h3>
        <p>
          Specifying your target audience helps the AI create content that resonates with your specific readers. 
          Different audiences have different pain points, language preferences, and buying behaviors.
        </p>
        
        <div className="not-prose my-6 bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-600" />
            <h4 className="font-medium">Available Target Audiences</h4>
          </div>
          <div className="p-4">
            <ul className="grid gap-3 md:grid-cols-2">
              <li className="p-3 border rounded-md">
                <div className="font-semibold">B2B Decision Makers</div>
                <p className="text-sm text-gray-600 mt-1">
                  Content focused on ROI, implementation challenges, and business solutions.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">General Consumers</div>
                <p className="text-sm text-gray-600 mt-1">
                  Accessible, value-focused content with personal benefits highlighted.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Enterprise Leaders</div>
                <p className="text-sm text-gray-600 mt-1">
                  Strategic, forward-thinking content focused on scalability and integration.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Small Business Owners</div>
                <p className="text-sm text-gray-600 mt-1">
                  Practical, resource-conscious advice with quick implementation ideas.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Technical Professionals</div>
                <p className="text-sm text-gray-600 mt-1">
                  Detailed, in-depth content with technical specifications and implementation details.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">C-Suite Executives</div>
                <p className="text-sm text-gray-600 mt-1">
                  High-level strategic content focused on business impact and competitive advantage.
                </p>
              </li>
            </ul>
          </div>
        </div>

        <h3>Content Goal</h3>
        <p>
          What do you want your content to achieve? Different goals require different approaches to structure, examples, and calls to action.
        </p>
        
        <div className="not-prose my-6 bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
            <Flag className="w-4 h-4 text-blue-600" />
            <h4 className="font-medium">Content Goals</h4>
          </div>
          <div className="p-4">
            <ul className="space-y-4">
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Brand Awareness</div>
                <p className="text-sm text-gray-600 mt-1">
                  Content that introduces your brand and positions you as a thought leader or solution provider.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Product Consideration</div>
                <p className="text-sm text-gray-600 mt-1">
                  Content that helps prospects evaluate your solution against alternatives or their current situation.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Direct Conversion</div>
                <p className="text-sm text-gray-600 mt-1">
                  Content designed to drive immediate action, with strong calls-to-action and value proposition.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Customer Loyalty</div>
                <p className="text-sm text-gray-600 mt-1">
                  Content that helps existing customers get more value from your product or service.
                </p>
              </li>
            </ul>
          </div>
        </div>

        <h3>Call-to-Action Type</h3>
        <p>
          The CTA type determines how strongly the content will push readers toward taking a specific action.
        </p>
        
        <div className="not-prose my-6 bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h4 className="font-medium">CTA Types</h4>
          </div>
          <div className="p-4">
            <ul className="space-y-3">
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-24 font-semibold">Soft CTA</div>
                <p className="text-sm text-gray-600">
                  Non-committal actions like "Subscribe" or "Learn More" - ideal for top-funnel content.
                </p>
              </li>
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-24 font-semibold">Medium CTA</div>
                <p className="text-sm text-gray-600">
                  Moderate commitment like "Free Trial" or "Download Guide" - good for middle-funnel content.
                </p>
              </li>
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-24 font-semibold">Hard CTA</div>
                <p className="text-sm text-gray-600">
                  High commitment actions like "Buy Now" or "Sign Up" - for bottom-funnel content.
                </p>
              </li>
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-24 font-semibold">Consultation</div>
                <p className="text-sm text-gray-600">
                  "Book a Consultation" or "Schedule a Demo" - for service-based businesses.
                </p>
              </li>
            </ul>
          </div>
        </div>

        <h3>Competitor Angle</h3>
        <p>
          How do you want to position your content in relation to competitors? This setting affects how your solution is presented relative to alternatives.
        </p>
        
        <div className="not-prose my-6 bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4">
            <ul className="space-y-3">
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-28 font-semibold">Direct Comparison</div>
                <p className="text-sm text-gray-600">
                  Explicitly compares your solution to specific competitors, highlighting differentiators.
                </p>
              </li>
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-28 font-semibold">Indirect Alternatives</div>
                <p className="text-sm text-gray-600">
                  Addresses alternative approaches without naming specific competitors.
                </p>
              </li>
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-28 font-semibold">Industry Transformation</div>
                <p className="text-sm text-gray-600">
                  Positions your solution as part of a larger industry shift, making alternatives seem outdated.
                </p>
              </li>
              <li className="flex gap-4 p-3 border rounded-md">
                <div className="w-28 font-semibold">Value Proposition</div>
                <p className="text-sm text-gray-600">
                  Focuses solely on your unique benefits without referencing alternatives.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6 mt-8">
        <h2 className="text-xl font-bold mb-4">Example: Marketing Options Interface</h2>
        <div className="bg-gray-50 p-6 border rounded-md">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-blue-700" />
            <span className="font-medium">Target Audience</span>
          </div>
          <div className="p-2 border rounded-md bg-white mb-4">
            <div className="flex justify-between items-center">
              <span>Select target audience</span>
              <span className="text-sm text-gray-500">B2B Decision Makers ▼</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            <Flag className="w-5 h-5 text-blue-700" />
            <span className="font-medium">Content Goal</span>
          </div>
          <div className="p-2 border rounded-md bg-white mb-4">
            <div className="flex justify-between items-center">
              <span>Select content goal</span>
              <span className="text-sm text-gray-500">Product Consideration ▼</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-blue-700" />
            <span className="font-medium">Call-to-Action Type</span>
          </div>
          <div className="p-2 border rounded-md bg-white">
            <div className="flex justify-between items-center">
              <span>Select CTA type</span>
              <span className="text-sm text-gray-500">Medium CTA ▼</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/writer/basic-options">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Basic Options
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/writer/writing-styles">
            Next: Writing Styles
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}